import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, type WebJob } from '../api';
import { Button } from '@/components/ui/button';
import { Task, type TaskStepView } from '@/components/ai-elements/task';

/** A job's progress in the chat (and on the home page): its steps, refreshed while it is open. */
export function JobProgress({ initial, canAct, onNavigate, defaultOpen = true }: { initial: WebJob; canAct: boolean;
  onNavigate?: () => void; defaultOpen?: boolean }) {
  const [job, setJob] = useState(initial);
  const [busy, setBusy] = useState(false);
  const active = job.status === 'working' || job.status === 'waiting';
  const id = job.id;
  const refresh = useRef(() => {});
  refresh.current = () => { void api<WebJob>(`/app-api/amy/jobs/${id}`).then(setJob).catch(() => {}); };
  useEffect(() => {
    if (!active) return;
    const tick = () => refresh.current();
    const timer = window.setInterval(tick, 5000);
    window.addEventListener('alder:state-changed', tick);
    return () => { window.clearInterval(timer); window.removeEventListener('alder:state-changed', tick); };
  }, [active]);
  const stop = async () => {
    setBusy(true);
    try { const out = await api<{ job: WebJob }>(`/app-api/amy/jobs/${id}/stop`, {}); setJob(out.job); toast.success('Stopped. Drafts already prepared stay in Needs you.'); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'This could not be stopped.'); } finally { setBusy(false); }
  };
  const undo = async (step: string) => {
    try { await api(`/app-api/amy/jobs/${id}/steps/${step}/undo`, {}); refresh.current(); toast.success('Undone: the promise is cancelled.'); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'This could not be undone.'); }
  };
  const steps: TaskStepView[] = job.steps.map(step => ({ id: step.id, title: step.title, status: step.status, label: step.label,
    detail: step.detail, action: step.needsYou
      ? <a className="amy-link" href={`#collections/${encodeURIComponent(step.needsYou)}`} onClick={onNavigate}>Open in Collections</a>
      : step.undo && canAct ? <Button variant="outline" size="sm" onClick={() => void undo(step.id)}>Undo</Button> : undefined }));
  const title = job.status === 'done' ? `Done: ${job.goal}` : job.status === 'stopped' ? `Stopped: ${job.goal}` : job.goal;
  return <div className="amy-job">
    <Task title={title} running={job.status === 'working'} steps={steps} defaultOpen={defaultOpen}
      footer={<>{job.summary.length > 0 && <p className="amy-job-summary">{job.summary.join(' ')}</p>}
        {active && canAct && job.requestedByMe && <Button variant="ghost" size="sm" disabled={busy} onClick={() => void stop()}>Stop this</Button>}</>} />
  </div>;
}

